import { styled } from '@mui/material';
import { Link } from 'react-router-dom';

const colors = {
  cyan: '#16b7db',
  dark: '#17284a',
  text: '#3a4a63',
  medium: '#5b6b82',
  muted: '#9aa7b8',
  border: '#e8ecf2',
  surface: '#ffffff',
  soft: '#f7f9fc',
};

export const Page = styled('section')({
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  color: colors.dark,
});

export const PageHeader = styled('header')({
  marginBottom: 16,
  display: 'flex',
  flexDirection: 'column',
});

export const Title = styled('h1')({
  margin: 0,
  color: colors.dark,
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1.2,
});

export const Subtitle = styled('p')({
  margin: '3px 0 0',
  color: '#8493a6',
  fontSize: 14,
  lineHeight: 1.4,
});

export const Content = styled('div')({
  minWidth: 0,
  minHeight: 0,
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 18,
});

export const TableCard = styled('div')({
  minWidth: 0,
  minHeight: 0,
  height: '100%',
  width: '100%',
  flex: '1 1 auto',
  overflow: 'hidden',
  border: `1px solid ${colors.border}`,
  borderRadius: 14,
  backgroundColor: colors.surface,
});

export const ActivityTable = styled('div')({
  height: '100%',
  width: '100%',
  minHeight: 0,
  '& th': {
    height: 48,
    color: colors.muted,
    backgroundColor: colors.soft,
    fontSize: 11.5,
    letterSpacing: '.6px',
    textTransform: 'uppercase',
  },
  '& td': {
    height: 58,
  },
});

export const DateCell = styled('span')({
  display: 'block',
  color: colors.dark,
  fontSize: 13.5,
  fontWeight: 600,
  '& small': { display: 'block', marginTop: 2, color: colors.muted, fontSize: 12.5, fontWeight: 400 },
});

export const DetailsCell = styled('span')({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TypeCell = styled('span')({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  color: colors.dark,
  fontSize: 14,
  fontWeight: 600,
});

export const TypeIcon = styled('span')({
  width: 28,
  height: 28,
  flex: '0 0 28px',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.cyan,
  backgroundColor: '#eef7fb',
  '& svg': { fontSize: 17 },
});

export const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: 'success' | 'warning' | 'error' | 'neutral' }>(({ tone }) => {
  const tones = {
    success: { backgroundColor: '#e4f6ee', color: '#2f9d70' },
    warning: { backgroundColor: '#fdf3e3', color: '#c68326' },
    error: { backgroundColor: '#fdeaee', color: '#d3567a' },
    neutral: { backgroundColor: '#eef1f5', color: colors.medium },
  };
  return {
    ...tones[tone],
    maxWidth: '100%',
    padding: '3px 10px',
    borderRadius: 11,
    display: 'inline-flex',
    overflow: 'hidden',
    fontSize: 12,
    fontWeight: 700,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
});

export const InitiatorCell = styled('span')({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  color: colors.dark,
  fontSize: 14,
  '& small': { color: colors.muted, fontSize: 11.5, fontWeight: 400, whiteSpace: 'nowrap' },
});

export const MiniAvatar = styled('span')({
  width: 26,
  height: 26,
  flex: '0 0 26px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.surface,
  backgroundColor: colors.cyan,
  fontSize: 10.5,
  fontWeight: 700,
});

export const DetailCard = styled('aside')({
  width: 260,
  maxHeight: '100%',
  flex: '0 0 260px',
  position: 'sticky',
  top: 16,
  overflow: 'auto',
  border: `1px solid ${colors.border}`,
  borderRadius: 14,
  backgroundColor: colors.surface,
});

export const EmptyDetail = styled('div')({
  minHeight: 180,
  padding: '40px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 8,
  '& strong': { fontSize: 15 },
  '& span': { color: '#8493a6', fontSize: 13.5, lineHeight: 1.5 },
});

export const DetailHeader = styled('div')({
  padding: '18px 20px 16px',
  borderBottom: '1px solid #f1f4f7',
});

export const DetailType = styled('div')({
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  color: colors.cyan,
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: '.7px',
});

export const DetailTitle = styled('h2')({
  margin: '0 0 10px',
  color: colors.dark,
  fontSize: 21,
  lineHeight: 1.2,
});

export const DetailMeta = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  flexWrap: 'wrap',
  color: '#8493a6',
  fontSize: 12.5,
});

export const DetailActions = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '0 20px 20px',
});

export const OpenRecordButton = styled(Link)({
  height: 36,
  padding: '0 14px',
  display: 'inline-flex',
  alignItems: 'center',
  border: `1px solid ${colors.border}`,
  borderRadius: 9,
  backgroundColor: colors.surface,
  color: colors.medium,
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const DetailFields = styled('dl')({
  margin: 0,
  padding: '6px 20px 14px',
  '& div': {
    padding: '10px 0',
    borderBottom: '1px solid #f4f7fa',
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    gap: 14,
    alignItems: 'baseline',
  },
  '& dt': {
    color: colors.muted,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.6px',
    textTransform: 'uppercase',
  },
  '& dd': { margin: 0, color: colors.text, fontSize: 13.5, overflowWrap: 'anywhere' },
});

export const FeedbackValue = styled('span', {
  shouldForwardProp: (prop) => prop !== 'hasFeedback',
})<{ hasFeedback: boolean }>(({ hasFeedback }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: hasFeedback ? '#2f9d70' : colors.muted,
  '& svg': { fontSize: 18 },
}));
